import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShipping as PickingIcon,
} from "@mui/icons-material";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import {
  useTSMDeactivateInvMovement,
  useTSMReactivateInvMovement,
} from "../../hooks/useTSInvMovement";
import { paginationDefaults } from "../../config/settingsDefaults";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import {
  DynamicRequestFilter,
  DynamicRequestSort,
} from "../../types/dynamicQueryTypes";

const FilterBox: React.FC<{
  finalSet: React.Dispatch<React.SetStateAction<string>>;
  placeholder: string;
}> = ({ finalSet, placeholder }) => {
  const [localVal, setLocalVal] = useState<string>("");
  return (
    <TextField
      label="Search"
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={(e) => finalSet(e.target.value)}
      placeholder={placeholder}
      slotProps={{
        input: {
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
          ),
        },
      }}
      sx={{ minWidth: 300 }}
    />
  );
};

export const PageInvMovList: React.FC = () => {
  const navigate = useNavigate();

  const { user } = useAuth();

  const [error, setError] = useState<string | null>(null);

  const [targetLot, setTargetLot] = useState<string>("");
  const [targetType, setTargetType] = useState<string>("");
  const [targetItem, setTargetItem] = useState<string>("");
  const [targetStatus, setTargetStatus] = useState<string>("");
  const [targetLoc, setTargetLoc] = useState<string>("");

  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });

  const [orderBy, setOrderBy] = useState<string>("movement_date");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const sorts: DynamicRequestSort[] = useMemo(() => {
    switch (orderBy) {
      case "movement_date":
        return [
          {
            table: "inventory_movements",
            alt_name: "movs",
            field: "movement_date",
            asc: order === "asc",
          },
        ];
      case "from_pos":
        return [
          {
            table: "locations",
            alt_name: "fromloc",
            field: "name",
            asc: order === "asc",
          },
          {
            table: "inventory_statuses",
            alt_name: "fromstat",
            field: "name",
            asc: order === "asc",
          },
        ];
      case "to_pos":
        return [
          {
            table: "locations",
            alt_name: "toloc",
            field: "name",
            asc: order === "asc",
          },
          {
            table: "inventory_statuses",
            alt_name: "tostat",
            field: "name",
            asc: order === "asc",
          },
        ];
      case "lot_num":
        return [
          {
            table: "inventory_lots",
            alt_name: "fromlot",
            field: "lot_number",
            asc: order === "asc",
          },
          {
            table: "inventory_lots",
            alt_name: "tolot",
            field: "lot_number",
            asc: order === "asc",
          },
        ];
      case "item_num":
        return [
          {
            table: "items",
            alt_name: "fromitem",
            field: "lot_number",
            asc: order === "asc",
          },
          {
            table: "items",
            alt_name: "toitem",
            field: "lot_number",
            asc: order === "asc",
          },
        ];
      case "quantity":
        return [
          {
            table: "inventory_movements",
            alt_name: "movs",
            field: "quantity",
            asc: order === "asc",
          },
        ];
      case "movement_type":
        return [
          {
            table: "inventory_movements",
            alt_name: "movs",
            field: "movement_type",
            asc: order === "asc",
          },
        ];
      default:
        return [];
    }
  }, [order, orderBy]);

  const filters: DynamicRequestFilter[][] = useMemo(() => {
    const filts: DynamicRequestFilter[][] = [];
    if (targetLot !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
        filts[1] = [];
      }
      filts[0].push({
        table: "inventory_lots",
        alt_name: "fromlot",
        field: "lot_number",
        operator: "contains",
        value: targetLot,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: false,
      });
      filts[1].push({
        table: "inventory_lots",
        alt_name: "tolot",
        field: "lot_number",
        operator: "contains",
        value: targetLot,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: false,
      });
    }
    if (targetType !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
        filts[1] = [];
      }
      filts[0].push({
        table: "inventory_movements",
        alt_name: "movs",
        field: "movement_type",
        operator: "contains",
        value: targetType,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      filts[1].push({
        table: "inventory_movements",
        alt_name: "movs",
        field: "movement_type",
        operator: "contains",
        value: targetType,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (targetItem !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
        filts[1] = [];
      }
      filts[0].push({
        table: "items",
        alt_name: "fromitem",
        field: "number",
        operator: "contains",
        value: targetItem,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      filts[1].push({
        table: "items",
        alt_name: "toitem",
        field: "number",
        operator: "contains",
        value: targetItem,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (targetStatus !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
        filts[1] = [];
      }
      filts[0].push({
        table: "inventory_statuses",
        alt_name: "fromstat",
        field: "name",
        operator: "contains",
        value: targetStatus,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      filts[1].push({
        table: "inventory_statuses",
        alt_name: "tostat",
        field: "name",
        operator: "contains",
        value: targetStatus,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (targetLoc !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
        filts[1] = [];
      }
      filts[0].push({
        table: "locations",
        alt_name: "fromloc",
        field: "name",
        operator: "contains",
        value: targetLoc,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      filts[1].push({
        table: "locations",
        alt_name: "toloc",
        field: "name",
        operator: "contains",
        value: targetLoc,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    return filts;
  }, [targetLot, targetType, targetItem, targetStatus, targetLoc]);

  const {
    data: movResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQDynamicList({
    page: pagination.page,
    page_size: pagination.size,
    primary: "inventory_movements",
    alt_name: "movs",
    primary_fields: ["id", "quantity", "movement_type", "movement_date"],
    joins: [
      {
        table: "uom_units",
        alt_name: "uom",
        fields: ["id", "unit_symbol"],
        condition: {
          remote_table: "inventory_movements",
          alt_name: "movs",
          remote_field: "uom_unit_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "inventory_positions",
        alt_name: "frompos",
        fields: ["id"],
        condition: {
          remote_table: "inventory_movements",
          alt_name: "movs",
          remote_field: "from_position_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "inventory_lots",
        alt_name: "fromlot",
        fields: ["id", "lot_number"],
        condition: {
          remote_table: "inventory_positions",
          alt_name: "frompos",
          remote_field: "inventory_lot_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "items",
        alt_name: "fromitem",
        fields: ["id", "number"],
        condition: {
          remote_table: "inventory_lots",
          alt_name: "fromlot",
          remote_field: "item_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "locations",
        alt_name: "fromloc",
        fields: ["id", "name"],
        condition: {
          remote_table: "inventory_positions",
          alt_name: "frompos",
          remote_field: "location_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "inventory_statuses",
        alt_name: "fromstat",
        fields: ["id", "name"],
        condition: {
          remote_table: "inventory_positions",
          alt_name: "frompos",
          remote_field: "status_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "inventory_positions",
        alt_name: "topos",
        fields: ["id"],
        condition: {
          remote_table: "inventory_movements",
          alt_name: "movs",
          remote_field: "to_position_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "inventory_lots",
        alt_name: "tolot",
        fields: ["id", "lot_number"],
        condition: {
          remote_table: "inventory_positions",
          alt_name: "topos",
          remote_field: "inventory_lot_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "items",
        alt_name: "toitem",
        fields: ["id", "number"],
        condition: {
          remote_table: "inventory_lots",
          alt_name: "tolot",
          remote_field: "item_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "locations",
        alt_name: "toloc",
        fields: ["id", "name"],
        condition: {
          remote_table: "inventory_positions",
          alt_name: "topos",
          remote_field: "location_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "inventory_statuses",
        alt_name: "tostat",
        fields: ["id", "name"],
        condition: {
          remote_table: "inventory_positions",
          alt_name: "topos",
          remote_field: "status_id",
          local_field: "id",
        },
        outer: true,
      },
    ],
    filters: filters,
    sorts: sorts,
    subqueries: [],
  });

  const deactivateMovMutation = useTSMDeactivateInvMovement();
  const reactivateMovMutation = useTSMReactivateInvMovement();

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to deactivate this Movement?")) {
      return;
    }

    try {
      await deactivateMovMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate Movement:", err);
      setError(err.response?.data?.detail || "Failed to deactivate Movement");
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivateMovMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to reactivate Movement:", err);
      setError(err.response?.data?.detail || "Failed to reactivate Movement");
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const [movements, movTotal]: [Record<string, any>[], number] = useMemo(() => {
    const movs: Record<string, any>[] = [];
    const total = movResponse?.total || 0;
    if (movResponse?.items) {
      movResponse.items.forEach((val, ind) => {
        movs.push(val);
      });
    }
    return [movs, total];
  }, [movResponse]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Inventory Movements
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/inventory/movements/form?mode=create")}
        >
          Move Inventory
        </Button>
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" flexWrap={"wrap"} gap={1}>
          <FilterBox finalSet={setTargetLot} placeholder="Filter By Lot" />
          <FilterBox finalSet={setTargetItem} placeholder="Filter By Item" />
          <FilterBox
            finalSet={setTargetType}
            placeholder="Filter By Movement Type"
          />
          <FilterBox finalSet={setTargetLoc} placeholder="Filter By Location" />
          <FilterBox
            finalSet={setTargetStatus}
            placeholder="Filter By Status"
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "lot_num" ? order : false}>
                <TableSortLabel
                  active={orderBy === "lot_num"}
                  direction={orderBy === "lot_num" ? order : "asc"}
                  onClick={() => handleSort("lot_num")}
                >
                  Lot Number
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "item_num" ? order : false}>
                <TableSortLabel
                  active={orderBy === "item_num"}
                  direction={orderBy === "item_num" ? order : "asc"}
                  onClick={() => handleSort("item_num")}
                >
                  Item Number
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "from_pos" ? order : false}>
                <TableSortLabel
                  active={orderBy === "from_pos"}
                  direction={orderBy === "from_pos" ? order : "asc"}
                  onClick={() => handleSort("from_pos")}
                >
                  From
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "to_pos" ? order : false}>
                <TableSortLabel
                  active={orderBy === "to_pos"}
                  direction={orderBy === "to_pos" ? order : "asc"}
                  onClick={() => handleSort("to_pos")}
                >
                  To
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "quantity" ? order : false}>
                <TableSortLabel
                  active={orderBy === "quantity"}
                  direction={orderBy === "quantity" ? order : "asc"}
                  onClick={() => handleSort("quantity")}
                >
                  Quantity Moved
                </TableSortLabel>
              </TableCell>
              <TableCell>UOM</TableCell>
              <TableCell
                sortDirection={orderBy === "movement_date" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "movement_date"}
                  direction={orderBy === "movement_date" ? order : "asc"}
                  onClick={() => handleSort("movement_date")}
                >
                  Movement Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "movement_type" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "movement_type"}
                  direction={orderBy === "movement_type" ? order : "asc"}
                  onClick={() => handleSort("movement_type")}
                >
                  Movement Type
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : movements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {filters.length > 0
                      ? "No movements found matching your search."
                      : "No inventory movements found."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              movements.map((mov: any) => {
                const lot_num =
                  mov["fromlot.lot_number"] ||
                  mov["tolot.lot_number"] ||
                  "UNKNOWN LOT NUMBER";
                const item_num =
                  mov["fromitem.number"] ||
                  mov["toitem.number"] ||
                  "UNKNOWN ITEM NUMBER";
                return (
                  <TableRow key={mov.id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        {lot_num}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        {item_num}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        {mov["fromloc.name"]
                          ? `${mov["fromloc.name"]} (${mov["fromstat.name"]})`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        {mov["toloc.name"]
                          ? `${mov["toloc.name"]} (${mov["tostat.name"]})`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>{mov["movs.quantity"]}</TableCell>
                    <TableCell>{mov["uom.unit_symbol"]}</TableCell>
                    <TableCell>
                      {mov["movs.movement_date"] &&
                        new Date(
                          mov["movs.movement_date"]
                        ).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{mov["movs.movement_type"]}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={movTotal}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};
